import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import SimpleToggle from "@/Components/UI/Forms/SimpleToggle";
import Modal from "@/Components/UI/Modal";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { CookieManagerContext } from "@/Contexts/CookieManagerContext";
import { Link } from "@/utils/navigation";
import { useContext, useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

type CookieSettingProps = {
    title: string;
    description: string;
    toggleInitialState?: boolean;
    toggleListener?: (isOn: boolean) => void;
    isToggleDisabled?: boolean;
};

function CookieSetting({
    title,
    description,
    toggleInitialState,
    toggleListener,
    isToggleDisabled,
}: CookieSettingProps) {
    const [isOpen, setIsOpen] = useState(false);

    const Arrow = isOpen ? IoIosArrowUp : IoIosArrowDown;

    return (
        <div>
            <div>
                <div className="flex items-center justify-between">
                    <div
                        className="grow cursor-pointer font-medium"
                        onClick={() => setIsOpen(!isOpen)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setIsOpen(!isOpen);
                            }
                        }}
                    >
                        <div
                            data-testid="cookie-categories-title"
                            className="flex items-center"
                        >
                            <Arrow className="mr-1 text-xl" /> {title}
                        </div>
                    </div>
                    {toggleListener === undefined ? (
                        <div>Always Active</div>
                    ) : (
                        <div>
                            <SimpleToggle
                                initialState={toggleInitialState}
                                onChangeListener={(isOn) =>
                                    toggleListener(isOn)
                                }
                                testId={`toggle-switch-${title.toLowerCase()}`}
                                disabled={isToggleDisabled}
                            />
                        </div>
                    )}
                </div>
                {isOpen && (
                    <div
                        data-testid="cookie-categories-text"
                        className="cursor-pointer pt-4 text-sm text-brand-black"
                        onClick={() => setIsOpen(false)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setIsOpen(false);
                            }
                        }}
                    >
                        {description}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function CookieSettings() {
    const cookieManager = useContext(CookieManagerContext);
    const analytics = useContext(AnalyticsContext);

    const closeCookieSettings = () => {
        cookieManager.resetCookieSettingsToLastSaved();
        if (!cookieManager.hasEverBeenSaved) {
            cookieManager.setShowCookieBanner(true);
        }
        cookieManager.setShowCookieSettingsModal(false);
    };

    const allowAll = () => {
        cookieManager.setAnalyticsToggleState(true);
        cookieManager.setAdvertisingToggleState(true);
        cookieManager.saveCookieSettingsAndCloseAll(
            {
                analytics: true,
                advertising: true,
            },
            analytics,
        );
    };

    const save = () => {
        cookieManager.saveCookieSettingsAndCloseAll(
            {
                analytics: cookieManager.analyticsSettingToggleState,
                advertising: cookieManager.advertisingSettingToggleState,
            },
            analytics,
        );
    };

    return (
        <Modal
            id="cookie-settings-modal"
            show={cookieManager.showCookieSettingsModal}
            showCloseButton={true}
            onClose={closeCookieSettings}
            testId={"cookie-settings-modal-container"}
        >
            <div className="relative max-w-lg rounded-lg bg-white">
                <div className="mb-4 px-8 pt-8">
                    <h1 className="text-xl font-semibold">Sharing Settings</h1>
                </div>
                <div
                    data-testid="cookie-settings-scroll-container"
                    className="flex max-h-[65vh] flex-col overflow-y-auto px-8 pb-16"
                >
                    <p
                        data-testid="cookie-settings-paragraph1"
                        className="mb-6 text-sm"
                    >
                        We use first party and third party cookies and similar
                        technologies to allow our site to function, to deliver
                        you coaching services, to personalize your experience,
                        to understand how users use our site, and for
                        advertising purposes. To learn more please see our{" "}
                        <Link
                            href={route("privacy")}
                            className="text-brand-red underline"
                        >
                            Privacy Policy
                        </Link>
                        .
                    </p>
                    <p
                        data-testid="cookie-settings-paragraph2"
                        className="mb-6 text-sm"
                    >
                        Below we explain the categories of cookies and tracking
                        that we utilize. To Allow all types, please click 'Allow
                        all'. To opt in or out of specific categories, please
                        update your settings and click 'Save settings'.
                    </p>
                    {cookieManager.isGpcActive ? (
                        <p
                            data-testid="gpc-notice"
                            className="mb-8 rounded-lg border border-brand-border p-4 text-sm"
                        >
                            Your browser is sending a Global Privacy Control
                            signal, so we've turned off analytics and
                            advertising cookies. To change this, turn off Global
                            Privacy Control in your browser, then update your
                            settings here.
                        </p>
                    ) : (
                        <div
                            data-testid="allow-all-container"
                            className="mb-8 flex grow justify-center"
                        >
                            <PrimaryButton
                                onClick={allowAll}
                                className="max-w-xs grow justify-center py-3"
                            >
                                Allow All
                            </PrimaryButton>
                        </div>
                    )}
                    <div className="mb-6 space-y-4">
                        {[
                            {
                                title: "Necessary",
                                description:
                                    "These first party and third party cookies and similar technologies are required for the site to function, to deliver you coaching services, to remember your settings and preferences, and to secure our website and applications. As a result, you cannot opt of these while using this site.",
                            },
                            {
                                title: "Analytics",
                                description:
                                    "These third party cookies and similar technologies are used by us to understand how users use our site and to personalize the experience to you.",
                                toggleInitialState:
                                    cookieManager.analyticsSettingToggleState,
                                toggleListener:
                                    cookieManager.setAnalyticsToggleState,
                                isToggleDisabled: cookieManager.isGpcActive,
                            },
                            {
                                title: "Advertising",
                                description:
                                    "We utilize third party advertising platforms to make people aware of our service. These third party cookies and similar technologies allow us to measure the effectiveness of ads and allow us to deliver more relevant and personalized ads.",
                                toggleInitialState:
                                    cookieManager.advertisingSettingToggleState,
                                toggleListener:
                                    cookieManager.setAdvertisingToggleState,
                                isToggleDisabled: cookieManager.isGpcActive,
                            },
                        ].map((category) => (
                            <CookieSetting {...category} key={category.title} />
                        ))}
                    </div>
                </div>
                {!cookieManager.isGpcActive && (
                    <div
                        data-testid="save-settings-container"
                        className="absolute bottom-0 flex w-full items-stretch justify-stretch bg-brand-white px-8"
                    >
                        <PrimaryButton
                            onClick={save}
                            className="mx-auto my-4 max-w-xs grow justify-center py-3"
                        >
                            Save settings
                        </PrimaryButton>
                    </div>
                )}
            </div>
        </Modal>
    );
}
